import { useState } from "react";
import { Star, ChevronRight, ChevronLeft, Send, X, SkipForward } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component - Tap-based for speed
const StarRating = ({ value, onChange, size = "lg" }) => {
  const [hover, setHover] = useState(0);
  const starSize = size === "lg" ? "w-10 h-10" : "w-8 h-8";

  return (
    <div className="flex gap-2 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className="p-1 transition-transform hover:scale-110 active:scale-95"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          data-testid={`star-${star}`}
        >
          <Star
            className={`${starSize} transition-colors ${
              star <= (hover || value)
                ? "fill-amber-400 text-amber-400"
                : "text-gray-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Progress indicator
const ProgressDots = ({ current, total }) => (
  <div className="flex gap-2 justify-center mb-6">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={`step-dot-${i}`}
        className={`w-2 h-2 rounded-full transition-colors ${
          i <= current ? "bg-[#0FA3B1]" : "bg-gray-200"
        }`}
      />
    ))}
  </div>
);

export default function ReviewWizard({ isOpen, onClose, lead, contractor, onSuccess }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  // Required ratings
  const [overallRating, setOverallRating] = useState(0);
  const [professionalRating, setProfessionalRating] = useState(0);
  const [onTimeRating, setOnTimeRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  
  // Optional ratings
  const [cleanupRating, setCleanupRating] = useState(0);
  const [asExpectedRating, setAsExpectedRating] = useState(0);
  
  // Optional feedback
  const [comment, setComment] = useState("");

  const totalSteps = 5; // Overall, Core, Cleanup, AsExpected, Feedback

  const handleSubmit = async () => {
    if (!overallRating || !professionalRating || !onTimeRating || !qualityRating) {
      toast.error("Please complete all required ratings");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/ratings`,
        {
          lead_id: lead.lead_id,
          contractor_id: contractor.contractor_id,
          overall_rating: overallRating,
          professional_rating: professionalRating,
          on_time_rating: onTimeRating,
          quality_rating: qualityRating,
          cleanup_rating: cleanupRating || null, // null if skipped
          as_expected_rating: asExpectedRating || null, // null if skipped
          comment: comment.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Thank you for your feedback!");
      onSuccess?.();
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit rating");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(0);
    setOverallRating(0);
    setProfessionalRating(0);
    setOnTimeRating(0);
    setQualityRating(0);
    setCleanupRating(0);
    setAsExpectedRating(0);
    setComment("");
  };

  const canProceed = () => {
    switch (step) {
      case 0: return overallRating > 0;
      case 1: return professionalRating > 0 && onTimeRating > 0 && qualityRating > 0;
      case 2: return true; // Optional - can skip
      case 3: return true; // Optional - can skip
      case 4: return true; // Optional - can skip
      default: return false;
    }
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSkip = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Auto-advance for single rating steps after selection
  const handleOverallSelect = (val) => {
    setOverallRating(val);
    setTimeout(() => setStep(1), 400);
  };

  const handleCleanupSelect = (val) => {
    setCleanupRating(val);
    setTimeout(() => setStep(3), 400);
  };

  const handleAsExpectedSelect = (val) => {
    setAsExpectedRating(val);
    setTimeout(() => setStep(4), 400);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetForm(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1F2933] text-center">
            {step === 0 && "How was your experience?"}
            {step === 1 && "Rate the contractor"}
            {step === 2 && "How was the cleanup?"}
            {step === 3 && "Was everything as expected?"}
            {step === 4 && "Anything else to share?"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {step === 0 && "Your feedback helps us prioritize great pros."}
            {step === 1 && "Did the contractor meet expectations in these areas?"}
            {step === 2 && "This is optional — skip if not applicable."}
            {step === 3 && "Was the final work and price as expected?"}
            {step === 4 && "Optional — feedback helps us improve."}
          </DialogDescription>
        </DialogHeader>

        <ProgressDots current={step} total={totalSteps} />

        <div className="min-h-[200px] flex flex-col justify-center">
          {/* Step 0: Overall Rating */}
          {step === 0 && (
            <div className="space-y-6 text-center animate-fade-in">
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="font-medium text-[#1F2933]">
                  {contractor?.contractor_name || "Contractor"}
                </p>
                <p className="text-sm text-[#1F2933]/60">
                  {lead?.category_name || "Service"}
                </p>
              </div>
              <p className="text-sm text-[#1F2933]/70">Overall, how was your experience?</p>
              <StarRating value={overallRating} onChange={handleOverallSelect} size="lg" />
            </div>
          )}

          {/* Step 1: Core Performance Ratings */}
          {step === 1 && (
            <div className="space-y-5 animate-fade-in">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1F2933]">Professional</span>
                  <span className="text-xs text-[#1F2933]/50">Communication, respect, punctuality</span>
                </div>
                <StarRating value={professionalRating} onChange={setProfessionalRating} size="sm" />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1F2933]">On Time</span>
                  <span className="text-xs text-[#1F2933]/50">Completed within timeline</span>
                </div>
                <StarRating value={onTimeRating} onChange={setOnTimeRating} size="sm" />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1F2933]">Quality</span>
                  <span className="text-xs text-[#1F2933]/50">Workmanship and durability</span>
                </div>
                <StarRating value={qualityRating} onChange={setQualityRating} size="sm" />
              </div>
            </div>
          )}

          {/* Step 2: Cleanup Rating (Optional) */}
          {step === 2 && (
            <div className="space-y-6 text-center animate-fade-in">
              <p className="text-sm text-[#1F2933]/70">How was the cleanup after the job?</p>
              <StarRating value={cleanupRating} onChange={handleCleanupSelect} size="lg" />
              <p className="text-xs text-[#1F2933]/40">Tap a star or skip this step</p>
            </div>
          )}

          {/* Step 3: Scope & Price Accuracy (Optional) */}
          {step === 3 && (
            <div className="space-y-6 text-center animate-fade-in">
              <p className="text-sm text-[#1F2933]/70">Was the final work and price as expected?</p>
              <StarRating value={asExpectedRating} onChange={handleAsExpectedSelect} size="lg" />
              <p className="text-xs text-[#1F2933]/40">Tap a star or skip this step</p>
            </div>
          )}

          {/* Step 4: Optional Written Feedback */}
          {step === 4 && (
            <div className="space-y-4 animate-fade-in">
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 750))}
                placeholder="Share any additional feedback... (optional)"
                className="min-h-[120px] resize-none"
                data-testid="review-comment"
              />
              <div className="flex justify-between items-center">
                <p className="text-xs text-[#1F2933]/40">
                  Optional — feedback helps us improve and reward great contractors.
                </p>
                <p className="text-xs text-[#1F2933]/50">
                  {comment.length}/750
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-3 mt-4">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1"
              data-testid="review-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          
          {step === 0 && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { resetForm(); onClose(); }}
            >
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
          
          {/* Skip button for optional steps */}
          {(step === 2 || step === 3) && (
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-[#1F2933]/60"
              data-testid="review-skip-btn"
            >
              <SkipForward className="w-4 h-4 mr-1" /> Skip
            </Button>
          )}
          
          {/* Continue/Submit button */}
          {step < totalSteps - 1 ? (
            <Button
              className="flex-1 bg-[#0FA3B1] hover:bg-[#0D8C98]"
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="review-next-btn"
            >
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="flex-1 bg-[#0FA3B1] hover:bg-[#0D8C98]"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="review-submit-btn"
            >
              {submitting ? (
                <div className="spinner w-5 h-5 border-2 border-white/20 border-t-white" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Submit
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
